/**
 * RoutingCategoryDialog - Criar/Editar categoria de Ação Rápida
 *
 * Reutiliza o RuleVisibilitySelector existente para que a categoria tenha
 * exatamente as mesmas opções (private/shared/public) das próprias regras.
 *
 * Sanitização: nome ≤ 80, descrição ≤ 240, sem HTML.
 *
 * CHANGELOG:
 *   2026-06-23 v1.0.0 - Versão inicial.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RuleVisibilitySelector, RuleVisibility } from '@/components/tenant/routing/RuleVisibilitySelector';
import { RoutingCategoryIconPicker } from './RoutingCategoryIconPicker';
import { RoutingCategoryIcon } from './RoutingCategoryIcon';
import {
  useRoutingRuleCategories,
  RoutingRuleCategory,
} from '@/hooks/useRoutingRuleCategories';
import { showToast } from '@/lib/toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Paleta de cores predefinida (mais segura para usuário comum)
const COLOR_OPTIONS = [
  '#f59e0b', // amber
  '#ef4444', // red
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#64748b', // slate
];

// Limites de input (sanitização)
const NAME_MAX = 80;
const DESCRIPTION_MAX = 240;

interface RoutingCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Categoria sendo editada (omitido para criação) */
  category?: RoutingRuleCategory | null;
  onSuccess?: () => void;
}

/**
 * Modal para criação/edição de categoria.
 */
export function RoutingCategoryDialog({
  open,
  onOpenChange,
  category,
  onSuccess,
}: RoutingCategoryDialogProps) {
  const { currentTenant } = useAuth();
  const { upsert, isUpserting } = useRoutingRuleCategories();

  const isEdit = !!category?.id;

  // Estado do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#f59e0b');
  const [icon, setIcon] = useState('Zap');
  const [visibility, setVisibility] = useState<RuleVisibility>('private');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Sincroniza estado quando abre / categoria muda
  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setDescription(category?.description ?? '');
    setColor(category?.color ?? '#f59e0b');
    setIcon(category?.icon ?? 'Zap');
    setVisibility((category?.visibility as RuleVisibility) ?? 'private');
    setSelectedUserIds([]);
  }, [open, category]);

  // Carrega allowed_users existentes em modo edição (shared)
  useEffect(() => {
    if (!open || !category?.id || !currentTenant?.id) return;
    if (category.visibility !== 'shared') return;

    (async () => {
      const { data } = await supabase
        .from('routing_rule_category_allowed_users')
        .select('user_id')
        .eq('category_id', category.id);
      if (data) setSelectedUserIds(data.map((d) => d.user_id));
    })();
  }, [open, category?.id, category?.visibility, currentTenant?.id]);

  // Submete o formulário
  const handleSubmit = async () => {
    if (!name.trim()) {
      showToast.error('Nome é obrigatório');
      return;
    }
    try {
      await upsert({
        id: category?.id,
        name: name.trim(),
        description: description.trim() || null,
        color,
        icon,
        visibility,
        allowed_user_ids: visibility === 'shared' ? selectedUserIds : undefined,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Toast tratado no hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEdit ? 'Editar Categoria' : 'Nova Categoria'}
          </DialogTitle>
          <DialogDescription>
            Organize as Ações Rápidas em categorias com cor, ícone e visibilidade.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-5 py-4">
            {/* Preview */}
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center"
                style={{ backgroundColor: `${color}20` }}
              >
                <RoutingCategoryIcon name={icon} color={color} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {name.trim() || 'Pré-visualização'}
                </p>
                {description && (
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="cat-name">Nome *</Label>
              <Input
                id="cat-name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
                placeholder="Ex: Boas-vindas"
                maxLength={NAME_MAX}
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="cat-desc">Descrição</Label>
              <Textarea
                id="cat-desc"
                value={description}
                onChange={(e) =>
                  setDescription(e.target.value.slice(0, DESCRIPTION_MAX))
                }
                placeholder="Descrição opcional..."
                rows={2}
                maxLength={DESCRIPTION_MAX}
              />
            </div>

            {/* Ícone + Cor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ícone</Label>
                <RoutingCategoryIconPicker
                  value={icon}
                  onChange={setIcon}
                  color={color}
                />
              </div>
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className="h-8 w-8 rounded-md border-2 transition-transform hover:scale-110"
                      style={{
                        backgroundColor: c,
                        borderColor: color === c ? c : 'transparent',
                        boxShadow:
                          color === c
                            ? `0 0 0 2px var(--background), 0 0 0 4px ${c}`
                            : undefined,
                      }}
                      aria-label={`Cor ${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Visibilidade (reutiliza componente existente) */}
            <RuleVisibilitySelector
              visibility={visibility}
              onVisibilityChange={setVisibility}
              selectedUserIds={selectedUserIds}
              onSelectedUserIdsChange={setSelectedUserIds}
            />
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 shrink-0 border-t mt-auto">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpserting}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isUpserting || !name.trim()}>
            {isUpserting
              ? 'Salvando...'
              : isEdit
              ? 'Salvar Alterações'
              : 'Criar Categoria'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
